import { Cancel, CheckCircle, Person, Sync } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useLookup } from "context/lookupContext";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { ColumnLabel } from "qubCommon/Inputs";
import FormCheckbox from "qubCommon/Inputs/Checkbox";
import ClientAutocomplete from "qubCommon/Inputs/ClientAutocomplete";
import ReviewSection from "qubCommon/ReviewSection";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import TextInput from "src/qubCommon/Inputs/TextInput";

const MothersInfo = ({
  applicant,
  review,
  canEdit = false,
  canApprove,
  canPartiallyEdit,
  canViewReview,
  canViewWSResult,
}) => {
  const { lookups } = useLookup();
  const form = useFormContext();
  const [checkInfo, setCheckInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();



  useEffect(() => {
    if (review?.wsResponse?.message) {
      setCheckInfo({
        status: review?.wsResponse?.status,
        bank: review?.wsResponse?.bank,
        timestamp: review?.wsResponse?.timestamp,
        message: review?.wsResponse?.message,
      });
    }
  }, [review]);

  useEffect(() => {
    if (applicant?.address?.municipality === null)
      form.setValue("applicant.address.municipality", null);
  }, [applicant]);

  const AccordionTitle = () => (
    <Stack direction="row" gap={1}>
      <Person color="primary" fontSize="small" sx={{ marginLeft: "2px" }} />

      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        ΣΤΟΙΧΕΙΑ ΑΙΤΟΥΣΑΣ
      </Typography>
      <Typography color="primary" sx={{ marginLeft: "18px", fontWeight: 500 }}>
        {applicant?.lastName} {applicant?.firstName}
      </Typography>
    </Stack>
  );

  useEffect(() => {
    if (!applicant?.address?.municipality)
      form.setValue("applicant.address.municipality", null);
  }, [applicant?.address?.municipality]);

  const renderResult = () => {
    if (!checkInfo) return null;

    const { status, timestamp, bank } = checkInfo;

    return (
      <Box mt={2}>
        {status === "success" ? (
          <Alert icon={<CheckCircle fontSize="inherit" />} severity="success">
            {checkInfo?.message}
          </Alert>
        ) : (
          <Alert icon={<Cancel fontSize="inherit" />} severity="error">
            {checkInfo?.message}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" mt={1}>
          Τελευταίος Έλεγχος: {timestamp}
        </Typography>
      </Box>
    );
  };

  const AccordionContent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.ama" label="Α.Μ.ΟΓΑ/ΕΦΚΑ*" />
      </Grid>
      <Grid item xs={6}>
        <Stack direction="column" gap={1}>
          <ColumnLabel label="Οικογένεια" />
          <FormCheckbox
            name="applicant.isMotherSingleParent"
            label="Μονογονεϊκή"
            disabled={!canEdit}
            changeFn={(e) => {
              if (e.target.checked) {
                form.setValue("applicant.husbandsHelp", []);
                form.setValue("applicant.husbands.created", []);
                form.setValue("applicant.husbands.updated", []);
                form.setValue("applicant.husbands.deleted", []);
              }
            }}
          />
        </Stack>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.amka" label="AMKA*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.afm" label="ΑΦΜ*" />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.firstName" label="Όνομα*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.lastName" label="Επώνυμο*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.fatherName" label="Πατρώνυμο*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.motherName" label="Μητρώνυμο*" />
      </Grid>
      {/* <Grid item xs={12} sm={6} sm={3}>
        <TextInput
          disabled
          name="applicant.fatherName"
          label="Πατρώνυμο"
          defaultValue={applicant?.fatherName}
        />
      </Grid> */}
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.phone1"
          label="Τηλέφωνο 1*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.phone2"
          label="Τηλέφωνο 2"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.email"
          label="Email*"
          disabled={!canEdit && !canPartiallyEdit}
          rules={{
            required: "Το email είναι υποχρεωτικό.",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Μη έγκυρη διεύθυνση email.",
            },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={8} md={8}>
        <TextInput
          name="applicant.address.addressLine"
          label="Διεύθυνση*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>

      <Grid item xs={12} sm={4} md={4} lg={4}>
        <TextInput
          name="applicant.address.postalCode"
          label="ΤΚ*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.address.city"
          label="Πόλη*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ClientAutocomplete
          name="applicant.address.municipality.id"
          label="Δήμος*"
          valueName="id"
          labelName="name"
          customChangeFn={(e) => {
            if (!e) form.setValue("applicant.address.municipality", null);
          }}
          options={lookups?.municipalities || []}
          defaultValue={applicant?.address?.municipality?.id}
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>


    </Grid>
  );

  return (
    <CustomSimpleAccordion
      title={<AccordionTitle />}
      content={<AccordionContent />}
    />
  );
};

export default MothersInfo;
